import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { useStorage } from '~Components/Storage';
import Modal from '~ComponentsImproved/Modal';
import { useLogger } from '~Hooks';
import AddNew from '~Scenes/Settings-v2/Scenes/Adyen/AddNew';
import AvailableTerminals from '~Scenes/Settings-v2/Scenes/Adyen/AvailableTerminals';
import { BlueButton, Box, OutlinedButton } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';
import useI18n from '~Translations';
import { AdyenStoreTerminals } from './__queries__';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #fff;
  box-shadow: 0px 0px 12px rgb(0 0 0 / 20%), 0px 20px 32px rgb(0 0 0 / 20%),
    0px 0px 40px rgb(0 0 0 / 16%);
  border-radius: 8px;
  box-sizing: border-box;
  min-height: 248px;
  overflow: auto;
`;

const InnerWrapper = styled.div`
  text-align: center;
  border-radius: 8px;
  padding: 44px 64px;
  box-sizing: border-box;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  min-height: 100%;

  ${handyBreakpoint} {
    padding: 44px 32px;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  margin-bottom: 16px;
  margin-top: 0;

  ${handyBreakpoint} {
    font-size: 24px;
  }
`;

const LargeText = styled.p`
  font-size: 24px;
  margin-bottom: 32px;
  margin-top: 0;

  ${handyBreakpoint} {
    font-size: 20px;
  }
`;

const Text = styled.p`
  font-size: 20px;
  margin-bottom: 16px;
  margin-top: 0;

  ${handyBreakpoint} {
    font-size: 16px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;

  button {
    width: 200px;
  }

  ${handyBreakpoint} {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

const FadeIn = styled.div`
  opacity: 0;
  animation: fade-in 0.2s forwards;
`;

const ADYEN_STORE_TERMINALS_QUERY = gql`
  query AdyenStoreTerminals {
    operativeViewer {
      id

      adyenStore {
        id
        terminals {
          id
        }
      }
    }
  }
`;

const BAE_ISSUES_CHECK = gql`
  query BaemingoIssues {
    user {
      id
    }
  }
`;

type Steps =
  | 'paymentStarted'
  | 'paymentMade'
  | 're-scan'
  | 'no-terminals'
  | 'cancel-info'
  | 'wait-info'
  | 'network-issues'
  | 'baemingo-issues';

const TITLE: Record<Steps, string> = {
  paymentStarted: 'AdyenWizard.PaymentStarted.Question',
  paymentMade: 'AdyenWizard.PaymentMade.Question',
  're-scan': 'AdyenWizard.LostConnection',
  'no-terminals': 'AdyenWizard.NoTerminal',
  'cancel-info': 'AdyenWizard.NetworkIssues.Cancel',
  'wait-info': 'AdyenWizard.NetworkIssues.Wait',
  'network-issues': 'AdyenWizard.NetworkIssues',
  'baemingo-issues': 'AdyenWizard.BaemingoIssues',
};

const ParagraphBox = styled(Box)`
  position: absolute;
  top: 32px;
  width: 100%;
  text-align: center;
  padding: 16px 0;
  box-sizing: border-box;
`;

interface Props {
  close: () => void;
  cancelPayment: () => void;
}

const AdyenWizard: FC<Props> = ({ close, cancelPayment }) => {
  const [, , venueId] = location.hash.split('/');
  const { i18n } = useI18n();
  const logger = useLogger('adyen-wizard');
  const [storage] = useStorage();
  const adyenTerminals = storage.adyenTerminals;
  const [step, setStep] = useState<Steps>('paymentStarted');
  const [hasInternetConnection, setHasInternetConnection] = useState<boolean>(true);
  const baeIssuesQuery = useQuery(BAE_ISSUES_CHECK, {
    fetchPolicy: 'network-only',
  });

  const adyenStoreTerminals = useQuery<AdyenStoreTerminals>(ADYEN_STORE_TERMINALS_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      venueId,
    },
  });

  const availableVenueTerminals =
    adyenStoreTerminals?.data?.operativeViewer?.adyenStore?.terminals || [];

  useEffect(() => {
    if (adyenTerminals.length === 0) {
      logger.info('No terminals has been added');
      setStep('no-terminals');
    }
  }, [adyenTerminals]);

  useEffect(() => {
    // Ping google to check if there is internet connection
    fetch('https://www.google.com', { mode: 'no-cors' })
      .then((res) => {
        setHasInternetConnection(true);
      })
      .catch((err) => {
        setHasInternetConnection(false);
      });
  }, []);

  useEffect(() => {
    if (!hasInternetConnection) {
      setStep('network-issues');
    } else if (!baeIssuesQuery.data && !baeIssuesQuery.loading) {
      setStep('baemingo-issues');
    }
  }, [baeIssuesQuery.data, hasInternetConnection]);

  const getSubText = () => {
    switch (step) {
      case 're-scan':
        return i18n('AdyenWizard.LostConnection.Text');
      case 'no-terminals':
        return i18n('AdyenWizard.NoTerminal.Text');
      default:
        return '';
    }
  };

  const isInfoStep =
    step === 'cancel-info' ||
    step === 'wait-info' ||
    step === 'network-issues' ||
    step === 'baemingo-issues';

  useEffect(() => {
    logger.info('Step changed', { step, adyenTerminals });
  }, [step]);

  return (
    <Modal isShowing={true} backdropClose hide={close} style={{ padding: '30px' }}>
      <Wrapper>
        <InnerWrapper>
          <FadeIn>
            <Box justifyContent="center" display="flex" alignItems="center" flexDirection="column">
              <ParagraphBox>
                <Paragraph level={4} fontWeight={600}>
                  {i18n('AdyenWizard.TopTitle')}
                </Paragraph>
              </ParagraphBox>
              {!isInfoStep && (
                <>
                  <Title>{i18n(TITLE[step])}</Title>
                  {getSubText() && <Text>{getSubText()}</Text>}
                </>
              )}

              {isInfoStep ? (
                <>
                  <LargeText>{i18n(TITLE[step])}</LargeText>
                  <BlueButton
                    style={{ width: '150px' }}
                    padding="large"
                    onTouchStart={() => {
                      if (step == 'cancel-info') {
                        cancelPayment();
                      }
                      close();
                    }}>
                    {i18n('AdyenWizard.OK')}
                  </BlueButton>
                </>
              ) : (
                <Content>
                  {step === 'paymentStarted' && (
                    <>
                      <OutlinedButton padding="large" onTouchStart={() => setStep('paymentMade')}>
                        {i18n('General.Yes')}
                      </OutlinedButton>
                      <OutlinedButton onTouchStart={() => setStep('re-scan')}>
                        {i18n('General.No')}
                      </OutlinedButton>
                    </>
                  )}

                  {step === 'paymentMade' && (
                    <>
                      <OutlinedButton padding="large" onTouchStart={() => setStep('wait-info')}>
                        {i18n('General.Yes')}
                      </OutlinedButton>
                      <OutlinedButton onTouchStart={() => setStep('cancel-info')}>
                        {i18n('General.No')}
                      </OutlinedButton>
                    </>
                  )}

                  {step === 're-scan' && (
                    <div>
                      <AddNew
                        onComplete={() => {
                          cancelPayment();
                          close();
                        }}
                        useCloud
                      />
                      <Box marginTop="large">
                        {adyenStoreTerminals.loading && <p>{i18n('General.LoadingDots')}</p>}
                        <AvailableTerminals
                          availableVenueTerminals={availableVenueTerminals}
                          addedTerminals={adyenTerminals}
                          onComplete={() => {
                            cancelPayment();
                            close();
                          }}
                        />
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center">
                        <OutlinedButton
                          padding="large"
                          onTouchStart={() => {
                            cancelPayment();
                            close();
                          }}>
                          {i18n('General.Cancel')}
                        </OutlinedButton>
                      </Box>
                    </div>
                  )}

                  {step === 'no-terminals' && (
                    <div>
                      <AddNew
                        useCloud
                        onComplete={() => {
                          cancelPayment();
                          close();
                        }}
                      />
                      <Box marginTop="large">
                        {adyenStoreTerminals.loading && <p>{i18n('General.LoadingDots')}</p>}
                        <AvailableTerminals
                          availableVenueTerminals={availableVenueTerminals}
                          addedTerminals={adyenTerminals}
                          onComplete={() => {
                            cancelPayment();
                            close();
                          }}
                        />
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center">
                        <OutlinedButton padding="large" onTouchStart={close}>
                          {i18n('General.Cancel')}
                        </OutlinedButton>
                      </Box>
                    </div>
                  )}
                </Content>
              )}
            </Box>
          </FadeIn>
        </InnerWrapper>
      </Wrapper>
    </Modal>
  );
};

export default AdyenWizard;
